/**
 * One lead: who, what they asked, the stage, the merge, and the trail
 * (FR-H2, FR-H6). A kit `Dialog` laid out as the Branches dialog is (C14
 * §10.6 item 7) — a lead has no page of its own and no address for one.
 *
 * The stage moves through a `Select` and one *Move* button rather than six
 * pressable chips. FR-H2 asks for a reason when a lead is lost, and the service
 * refuses `lost` without one, so the reason field appears when *Lost* is chosen
 * and *Move* says why it is disabled until it is filled.
 *
 * **Merge asks first**, but not from in here: a confirmation stacked on a
 * dialog is dismissed by the dialog under it, so `onMerge` hands the pair to
 * the page, which closes this dialog, confirms, and reopens the survivor.
 */

import {
  Alert,
  Button,
  Dialog,
  Input,
  Select,
  Skeleton,
  formatDateTime,
} from '@fiveox/ui'
import { Link2 } from 'lucide-react'
import { type ReactNode, useCallback, useEffect, useState } from 'react'

import { StatusBadge } from '../../components/page'
import { ApiError } from '../../lib/api'
import {
  LEAD_STAGES,
  type LeadDetail,
  type LeadRecord,
  type LeadStage,
  SOURCE_LABELS,
  STAGE_LABELS,
  arrivalNote,
  canMove,
  crm,
  leadSubtitle,
} from '../../lib/api/crm'

/** A section heading inside the dialog. */
function Part({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-border border-t pt-4">
      <h3 className="text-foreground mb-3 text-sm font-semibold">{title}</h3>
      {children}
    </section>
  )
}

export function LeadDialog({
  leadId,
  mayUpdate,
  mayMerge,
  serviceNames,
  onClose,
  onChanged,
  onMerge,
}: {
  leadId: string
  mayUpdate: boolean
  mayMerge: boolean
  serviceNames: Record<string, string>
  onClose: () => void
  onChanged: () => void | Promise<void>
  /** Retire `other` into `into` — the page confirms first. */
  onMerge: (other: LeadRecord, into: LeadRecord) => void
}) {
  const [lead, setLead] = useState<LeadDetail | null>(null)
  const [dupes, setDupes] = useState<LeadRecord[]>([])
  const [stage, setStage] = useState<LeadStage | ''>('')
  const [lostReason, setLostReason] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState<'stage' | 'note' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const [detail, duplicates] = await Promise.all([crm.get(leadId), crm.duplicates(leadId)])
      setLead(detail.data)
      setDupes(duplicates.data)
      setStage(detail.data.stage)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That lead could not be loaded.')
    }
  }, [leadId])

  useEffect(() => {
    void load()
  }, [load])

  const act = async (kind: 'stage' | 'note', fn: () => Promise<unknown>) => {
    setBusy(kind)
    setError(null)
    try {
      await fn()
      await load()
      await onChanged()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.')
    } finally {
      setBusy(null)
    }
  }

  if (!lead) {
    return (
      <Dialog open onClose={onClose} title="Lead" error={error} width="lg">
        {!error && (
          <div role="status" aria-busy aria-label="Loading" className="space-y-3">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-16 w-full rounded-lg" />
            <Skeleton className="h-9 w-full rounded-lg" />
          </div>
        )}
      </Dialog>
    )
  }

  const serviceName = lead.service_id ? serviceNames[lead.service_id] : null
  const movable = mayUpdate && canMove(lead)
  const needsReason = stage === 'lost' && !lostReason.trim()
  const moveWhy =
    stage === lead.stage
      ? 'Choose a different stage to move it.'
      : needsReason
        ? 'Say why it was lost first.'
        : undefined

  return (
    <Dialog open onClose={onClose} title={lead.full_name} error={error} width="lg">
      <div className="space-y-5">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge kind="lead" status={lead.status === 'merged' ? 'merged' : lead.stage} />
            <span className="text-muted-foreground text-xs">{SOURCE_LABELS[lead.source]}</span>
          </div>
          {leadSubtitle(lead, serviceName) && (
            <p className="text-muted-foreground text-sm">{leadSubtitle(lead, serviceName)}</p>
          )}
          {arrivalNote(lead) && <p className="text-muted-foreground text-xs">{arrivalNote(lead)}</p>}
          {lead.message && (
            <blockquote className="border-border text-foreground border-l-2 pl-3 text-sm whitespace-pre-wrap">
              {lead.message}
            </blockquote>
          )}
          {lead.stage === 'lost' && lead.lost_reason && (
            <p className="text-muted-foreground text-sm">Lost: {lead.lost_reason}</p>
          )}
        </div>

        {lead.status === 'merged' && (
          <Alert tone="info">
            This lead was merged into another one. Work the surviving lead instead.
          </Alert>
        )}

        {movable && (
          <Part title="Stage">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="sm:w-56">
                <Select
                  label="Move it to"
                  value={stage}
                  onChange={(event) => setStage(event.target.value as LeadStage)}
                >
                  {LEAD_STAGES.map((s) => (
                    <option key={s} value={s}>
                      {STAGE_LABELS[s]}
                      {s === lead.stage ? ' (now)' : ''}
                    </option>
                  ))}
                </Select>
              </div>
              <span title={moveWhy}>
                <Button
                  disabled={moveWhy !== undefined || busy !== null}
                  loading={busy === 'stage'}
                  onClick={() =>
                    void act('stage', () =>
                      crm.setStage(
                        lead.id,
                        stage as LeadStage,
                        stage === 'lost' ? lostReason : undefined,
                      ),
                    )
                  }
                >
                  Move to {stage ? STAGE_LABELS[stage] : '…'}
                </Button>
              </span>
            </div>
            {/* FR-H2: "with reasons for lost". The service refuses `lost`
                without one, so the field is here rather than a refusal the
                person has to decode. */}
            {stage === 'lost' && (
              <div className="mt-3">
                <Input
                  label="Why was it lost?"
                  value={lostReason}
                  maxLength={300}
                  onChange={(event) => setLostReason(event.target.value)}
                  hint="Needed to mark a lead lost — it is how you learn whether it was price, timing or scope."
                />
              </div>
            )}
          </Part>
        )}

        {mayMerge && dupes.length > 0 && (
          <Part title="Possibly the same person">
            <p className="text-muted-foreground mb-3 flex items-start gap-2 text-sm">
              <Link2 size={16} aria-hidden className="mt-0.5 shrink-0" />
              These share an email address, a telephone number or a PAN with this lead. Merging keeps
              both records and both trails — nothing is deleted.
            </p>
            <ul className="space-y-2">
              {dupes.map((other) => (
                <li
                  key={other.id}
                  className="border-border flex flex-wrap items-center justify-between gap-3 rounded-lg border px-3 py-2"
                >
                  <span className="min-w-0 text-sm">
                    <span className="text-foreground font-medium">{other.full_name}</span>{' '}
                    <span className="text-muted-foreground text-xs">{leadSubtitle(other)}</span>
                  </span>
                  <Button variant="outline" size="sm" onClick={() => onMerge(other, lead)}>
                    Merge into this one
                  </Button>
                </li>
              ))}
            </ul>
          </Part>
        )}

        <Part title="What has happened">
          {mayUpdate && (
            <form
              className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start"
              onSubmit={(event) => {
                event.preventDefault()
                if (!note.trim()) return
                void act('note', async () => {
                  await crm.logActivity(lead.id, { kind: 'note', summary: note })
                  setNote('')
                })
              }}
            >
              <div className="min-w-0 flex-1">
                <Input
                  aria-label="Add a note"
                  value={note}
                  maxLength={300}
                  onChange={(event) => setNote(event.target.value)}
                  placeholder="Add a note — a call, what they said"
                />
              </div>
              <Button
                type="submit"
                variant="outline"
                disabled={!note.trim() || busy !== null}
                loading={busy === 'note'}
              >
                Add note
              </Button>
            </form>
          )}
          {lead.activities.length === 0 ? (
            <p className="text-muted-foreground text-sm">Nothing recorded yet.</p>
          ) : (
            <ol className="space-y-3">
              {lead.activities.map((activity) => (
                <li key={activity.id} className="text-sm">
                  <p className="text-foreground">{activity.summary}</p>
                  <p className="text-muted-foreground text-xs">
                    {formatDateTime(activity.occurred_at)}
                    {activity.actor_kind === 'visitor' && ' · from your website'}
                  </p>
                </li>
              ))}
            </ol>
          )}
        </Part>
      </div>
    </Dialog>
  )
}
